
import React from 'react';
import { Button } from "@/components/ui/button";
import { 
  Layers,
  Plus,
  Settings,
  ChevronDown,
  ChevronRight,
  Monitor,
  Link,
  Unlink
} from "lucide-react";
import { useRrwebSessions } from '@/hooks/useRrwebSessions';
import { useProjectRrwebSessions, useCreateProjectRrwebSession } from '@/hooks/useProjectRrwebSessions';
import SessionCard from './sessions/SessionCard';
import SessionPreview from './sessions/SessionPreview';
import { toast } from 'sonner';

interface SidebarProps {
  projectId: string;
}

const Sidebar = ({ projectId }: SidebarProps) => {
  const { data: allSessions } = useRrwebSessions();
  const { data: projectSessions } = useProjectRrwebSessions(projectId);
  const createProjectSession = useCreateProjectRrwebSession();
  
  const [availableExpanded, setAvailableExpanded] = React.useState(true);
  const [linkedExpanded, setLinkedExpanded] = React.useState(true);
  const [previewSession, setPreviewSession] = React.useState(null);

  // Get sessions that are not linked to this project
  const availableSessions = React.useMemo(() => {
    if (!allSessions || !projectSessions) return [];
    const linkedSessionIds = projectSessions.map(ps => ps.rrweb_session_id);
    return allSessions.filter(session => !linkedSessionIds.includes(session.id));
  }, [allSessions, projectSessions]);

  // Get sessions that are linked to this project
  const linkedSessions = React.useMemo(() => {
    if (!allSessions || !projectSessions) return [];
    return allSessions.filter(session => 
      projectSessions.some(ps => ps.rrweb_session_id === session.id)
    );
  }, [allSessions, projectSessions]);

  const handleLinkSession = async (session) => {
    try {
      await createProjectSession.mutateAsync({
        project_id: projectId,
        rrweb_session_id: session.id,
        order_in_project: (projectSessions?.length || 0) + 1,
      });
      toast.success('Session linked to project');
    } catch (error) {
      console.error('Failed to link session:', error);
      toast.error('Failed to link session');
    }
  };

  return (
    <div className="w-80 bg-editor-medium border-r border-editor-light flex flex-col h-full">
      {/* Header */}
      <div className="p-4 border-b border-editor-light">
        <div className="flex items-center justify-between">
          <h3 className="font-semibold text-white flex items-center">
            <Layers className="h-4 w-4 mr-2" />
            Sessions
          </h3>
          <Button variant="ghost" size="icon" className="h-6 w-6">
            <Plus className="h-3 w-3" />
          </Button>
        </div>
        <p className="text-xs text-gray-400 mt-1">
          Manage recording sessions for this project
        </p>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto">
        {/* Linked Sessions */}
        <div className="p-3">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setLinkedExpanded(!linkedExpanded)}
            className="w-full justify-start text-gray-300 hover:text-white mb-2"
          >
            {linkedExpanded ? <ChevronDown className="h-3 w-3 mr-1" /> : <ChevronRight className="h-3 w-3 mr-1" />}
            Project Sessions ({linkedSessions.length})
          </Button>
          
          {linkedExpanded && (
            <div className="space-y-2 ml-4">
              {linkedSessions.length === 0 ? (
                <div className="text-center py-4">
                  <Monitor className="h-8 w-8 text-gray-500 mx-auto mb-2" />
                  <p className="text-xs text-gray-500">No sessions linked</p>
                  <p className="text-xs text-gray-600 mt-1">
                    Link sessions from below
                  </p>
                </div>
              ) : (
                linkedSessions.map(session => (
                  <div key={session.id} className="relative group">
                    <SessionCard
                      session={session}
                      onPreview={setPreviewSession}
                      showActions={false}
                    />
                    <Button
                      size="sm"
                      variant="ghost"
                      className="absolute top-1 right-1 opacity-0 group-hover:opacity-100 h-6 w-6 p-0 bg-red-600/80 hover:bg-red-600"
                      title="Unlink session"
                    >
                      <Unlink className="h-3 w-3" />
                    </Button>
                  </div>
                ))
              )}
            </div>
          )}
        </div>

        {/* Available Sessions */}
        <div className="p-3 border-t border-editor-light">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setAvailableExpanded(!availableExpanded)}
            className="w-full justify-start text-gray-300 hover:text-white mb-2"
          >
            {availableExpanded ? <ChevronDown className="h-3 w-3 mr-1" /> : <ChevronRight className="h-3 w-3 mr-1" />}
            Available Sessions ({availableSessions.length})
          </Button>
          
          {availableExpanded && (
            <div className="space-y-2 ml-4">
              {availableSessions.length === 0 ? (
                <div className="text-center py-4">
                  <Monitor className="h-8 w-8 text-gray-500 mx-auto mb-2" />
                  <p className="text-xs text-gray-500">No sessions available</p>
                  <p className="text-xs text-gray-600 mt-1">
                    Create recordings to see them here
                  </p>
                </div>
              ) : (
                availableSessions.map(session => (
                  <div key={session.id} className="relative group">
                    <SessionCard
                      session={session}
                      onPreview={setPreviewSession}
                      showActions={false}
                    />
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleLinkSession(session)}
                      className="absolute top-1 right-1 opacity-0 group-hover:opacity-100 h-6 w-6 p-0 bg-editor-accent/80 hover:bg-editor-accent"
                      title="Link to project"
                    >
                      <Link className="h-3 w-3" />
                    </Button>
                  </div>
                ))
              )}
            </div>
          )}
        </div>
      </div>

      {/* Footer */}
      <div className="p-3 border-t border-editor-light">
        <Button variant="ghost" size="icon" className="w-full justify-center">
          <Settings className="h-4 w-4" />
        </Button>
      </div>

      {/* Session Preview Modal */}
      <SessionPreview
        session={previewSession}
        open={!!previewSession}
        onOpenChange={(open) => !open && setPreviewSession(null)}
      />
    </div>
  );
};

export default Sidebar;
